import { Capacitor } from '@capacitor/core';

/**
 * Comprehensive OneSignal diagnostic utility
 */
export const runOneSignalDiagnostics = async (): Promise<{
  success: boolean;
  issues: string[];
  recommendations: string[];
  deviceInfo: any;
  pluginInfo: any;
}> => {
  const issues: string[] = [];
  const recommendations: string[] = [];
  let deviceInfo: any = {};
  let pluginInfo: any = {};

  try {
    // Check if running on native platform
    if (!Capacitor.isNativePlatform()) {
      issues.push('Not running on native platform - OneSignal only works on iOS/Android');
      recommendations.push('Deploy to a real device or simulator to test OneSignal');
      return {
        success: false,
        issues,
        recommendations,
        deviceInfo,
        pluginInfo
      };
    }

    // Get device info
    try {
      const { Device } = await import('@capacitor/device');
      deviceInfo = await Device.getInfo();
    } catch (error) {
      issues.push('Could not get device information');
    }

    // Check for OneSignal App ID
    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      issues.push('VITE_ONESIGNAL_APP_ID environment variable not set');
      recommendations.push('Set VITE_ONESIGNAL_APP_ID in your environment variables');
    } else {
      pluginInfo.appId = oneSignalAppId;
    }

    // Check for window.plugins object
    if (typeof window === 'undefined') {
      issues.push('Window object not available');
      return {
        success: false,
        issues,
        recommendations,
        deviceInfo,
        pluginInfo
      };
    }

    if (!window.plugins) {
      issues.push('window.plugins object not found');
      recommendations.push('OneSignal plugin may not be properly installed');
      recommendations.push('Run: npm install onesignal-cordova-plugin');
      recommendations.push('Run: npx cap sync');
    } else {
      pluginInfo.pluginsAvailable = Object.keys(window.plugins);
    }

    // Check for OneSignal plugin specifically
    if (!window.plugins?.OneSignal) {
      issues.push('OneSignal plugin not found in window.plugins');
      recommendations.push('Check if onesignal-cordova-plugin is properly installed');
      recommendations.push('Verify capacitor.config.ts includes OneSignal configuration');
    } else {
      // Analyze OneSignal plugin structure
      const oneSignal = window.plugins.OneSignal;
      pluginInfo.oneSignalMethods = Object.getOwnPropertyNames(oneSignal);
      
      // Check for SDK version indicators
      if (oneSignal.User && oneSignal.Notifications) {
        pluginInfo.sdkVersion = '5.x (Modern)';
        pluginInfo.hasUser = !!oneSignal.User;
        pluginInfo.hasNotifications = !!oneSignal.Notifications;
        
        // Check User methods
        if (oneSignal.User) {
          pluginInfo.userMethods = Object.getOwnPropertyNames(oneSignal.User);
        }
        
        // Check Notifications methods
        if (oneSignal.Notifications) {
          pluginInfo.notificationMethods = Object.getOwnPropertyNames(oneSignal.Notifications);
        }
      } else if (oneSignal.getDeviceState) {
        pluginInfo.sdkVersion = '3.x/4.x (Legacy)';
        pluginInfo.hasGetDeviceState = typeof oneSignal.getDeviceState === 'function';
        pluginInfo.hasSetAppId = typeof oneSignal.setAppId === 'function';
        pluginInfo.hasPromptForPermissions = typeof oneSignal.promptForPushNotificationsWithUserResponse === 'function';
      } else {
        pluginInfo.sdkVersion = 'Unknown/Incompatible';
        issues.push('OneSignal plugin structure not recognized');
        recommendations.push('Update onesignal-cordova-plugin to latest version');
      }
    }

    // Check for Cordova
    if (window.cordova) {
      pluginInfo.hasCordova = true;
      pluginInfo.cordovaVersion = window.cordova.version;
      
      if (window.cordova.exec) {
        pluginInfo.hasCordovaExec = true;
        
        // Test OneSignal Cordova service
        try {
          await new Promise((resolve, reject) => {
            window.cordova.exec(
              (result: any) => {
                pluginInfo.cordovaOneSignalTest = 'Available';
                resolve(result);
              },
              (error: any) => {
                pluginInfo.cordovaOneSignalTest = `Error: ${error}`;
                resolve(null);
              },
              'OneSignalPush',
              'test',
              [],
              { timeout: 5000 }
            );
          });
        } catch (cordovaError) {
          pluginInfo.cordovaOneSignalTest = 'Not available or timeout';
        }
      } else {
        issues.push('Cordova exec function not available');
      }
    } else {
      pluginInfo.hasCordova = false;
    }

    // Test OneSignal initialization
    try {
      const { initializeOneSignal } = await import('@/integrations/onesignal');
      pluginInfo.canImportOneSignal = true;
      
      // Try to initialize (but don't throw if it fails)
      try {
        await initializeOneSignal();
        pluginInfo.initializationStatus = 'Success';
      } catch (initError) {
        pluginInfo.initializationStatus = `Failed: ${initError.message}`;
        issues.push(`OneSignal initialization failed: ${initError.message}`);
      }
    } catch (importError) {
      issues.push('Cannot import OneSignal integration module');
      pluginInfo.canImportOneSignal = false;
    }

    // Check permissions
    try {
      const { getNotificationPermissionStatus } = await import('@/integrations/onesignal');
      const hasPermission = await getNotificationPermissionStatus();
      pluginInfo.hasNotificationPermission = hasPermission;
      
      if (!hasPermission) {
        recommendations.push('Request notification permissions from user');
        recommendations.push('Users need to grant permission to receive push notifications');
      }
    } catch (permissionError) {
      issues.push('Cannot check notification permissions');
    }

    // Check database
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data: userData } = await supabase
          .from('users')
          .select('onesignal_user_id')
          .eq('id', user.id)
          .single();
        
        pluginInfo.currentUserHasOneSignalId = !!userData?.onesignal_user_id;
        
        if (!userData?.onesignal_user_id) {
          issues.push('Current user does not have OneSignal ID in database');
          recommendations.push('Run OneSignal migration to update user ID');
        }
      } else {
        pluginInfo.userAuthenticated = false;
        recommendations.push('User must be authenticated to save OneSignal ID');
      }
    } catch (dbError) {
      issues.push('Cannot check database for OneSignal ID');
    }

    return {
      success: issues.length === 0,
      issues,
      recommendations,
      deviceInfo,
      pluginInfo
    };

  } catch (error) {
    issues.push(`Diagnostic failed: ${error.message}`);
    return {
      success: false,
      issues,
      recommendations,
      deviceInfo,
      pluginInfo
    };
  }
};

/**
 * Generate a comprehensive diagnostic report
 */
export const generateDiagnosticReport = async (): Promise<string> => {
  const result = await runOneSignalDiagnostics();
  
  let report = '=== OneSignal Diagnostic Report ===\n\n';
  
  report += `Status: ${result.success ? '✅ OK' : '❌ Issues Found'}\n\n`;
  
  if (result.deviceInfo.platform) {
    report += '--- Device Information ---\n';
    report += `Platform: ${result.deviceInfo.platform}\n`;
    report += `OS Version: ${result.deviceInfo.osVersion}\n`;
    report += `Model: ${result.deviceInfo.model}\n`;
    report += `Is Virtual: ${result.deviceInfo.isVirtual}\n\n`;
  }
  
  report += '--- Plugin Information ---\n';
  report += `SDK Version: ${result.pluginInfo.sdkVersion || 'Unknown'}\n`;
  report += `OneSignal App ID: ${result.pluginInfo.appId ? '✅ Set' : '❌ Missing'}\n`;
  report += `Can Import Module: ${result.pluginInfo.canImportOneSignal ? '✅ Yes' : '❌ No'}\n`;
  report += `Initialization: ${result.pluginInfo.initializationStatus || 'Not attempted'}\n`;
  report += `Has Notification Permission: ${result.pluginInfo.hasNotificationPermission ? '✅ Yes' : '❌ No'}\n`;
  report += `Current User Has OneSignal ID: ${result.pluginInfo.currentUserHasOneSignalId ? '✅ Yes' : '❌ No'}\n\n`;
  
  if (result.pluginInfo.oneSignalMethods) {
    report += `Available Methods: ${result.pluginInfo.oneSignalMethods.join(', ')}\n\n`;
  }
  
  if (result.issues.length > 0) {
    report += '--- Issues Found ---\n';
    result.issues.forEach((issue, index) => {
      report += `${index + 1}. ${issue}\n`;
    });
    report += '\n';
  }
  
  if (result.recommendations.length > 0) {
    report += '--- Recommendations ---\n';
    result.recommendations.forEach((rec, index) => {
      report += `${index + 1}. ${rec}\n`;
    });
    report += '\n';
  }
  
  report += '--- Technical Details ---\n';
  report += JSON.stringify(result.pluginInfo, null, 2);
  
  return report;
}; 